/**
 * SSP Calculation Utilities
 * Handles all Statutory Sick Pay calculations for both current and 2026 rules
 */

import {
  SSP_WEEKLY_RATE,
  SSP_WEEKLY_RATE_2026,
  LOWER_EARNINGS_LIMIT,
  NEW_EARNINGS_CAP_PERCENT,
  WAITING_DAYS_CURRENT,
  WAITING_DAYS_2026,
  WEEKS_FOR_AWE,
  EIGHTY_PERCENT_THRESHOLD_2026,
} from '../data/constants';

/**
 * Calculate Average Weekly Earnings from earnings history
 * @param {number[]} earningsData - Array of earnings (weekly or monthly)
 * @param {object} options - Calculation options
 * @returns {object} AWE calculation result
 */
export function calculateAWE(earningsData, options = {}) {
  const {
    payFrequency = 'weekly',
    weeksEmployed = 8,
  } = options;

  // Ensure we have the earnings array
  const earnings = [...earningsData];

  let awe, total, method, periodCount;

  if (payFrequency === 'monthly') {
    // Monthly-paid: (Total earnings ÷ months) × 12 ÷ 52
    // Count non-zero months in the relevant period
    const months = earnings.filter(e => e > 0).length || 1;
    total = earnings.reduce((sum, e) => sum + e, 0);
    awe = (total / months) * 12 / 52;
    method = `Monthly: (£${total.toFixed(2)} ÷ ${months} months) × 12 ÷ 52`;
    periodCount = months;
  } else if (weeksEmployed < 8) {
    // New employee: Less than 8 weeks employed
    const actualWeeks = Math.min(weeksEmployed, earnings.length);
    const relevantEarnings = earnings.slice(-actualWeeks);
    total = relevantEarnings.reduce((sum, e) => sum + e, 0);
    awe = actualWeeks > 0 ? total / actualWeeks : 0;
    method = `New Employee: £${total.toFixed(2)} ÷ ${actualWeeks} weeks`;
    periodCount = actualWeeks;
  } else {
    // Standard weekly: Sum of 8 weeks ÷ 8
    while (earnings.length < WEEKS_FOR_AWE) {
      earnings.unshift(0);
    }
    const relevantEarnings = earnings.slice(-WEEKS_FOR_AWE);
    total = relevantEarnings.reduce((sum, e) => sum + e, 0);
    awe = total / WEEKS_FOR_AWE;
    method = `Weekly: £${total.toFixed(2)} ÷ ${WEEKS_FOR_AWE} weeks`;
    periodCount = WEEKS_FOR_AWE;
  }

  return {
    earnings: earnings.slice(-Math.max(periodCount, WEEKS_FOR_AWE)),
    total,
    awe,
    meetsLEL: awe >= LOWER_EARNINGS_LIMIT,
    lelShortfall: awe < LOWER_EARNINGS_LIMIT ? LOWER_EARNINGS_LIMIT - awe : 0,
    payFrequency,
    weeksEmployed,
    method,
    periodCount,
  };
}

/**
 * Calculate daily SSP rate based on qualifying days
 * @param {number} qualifyingDays - Number of qualifying days per week (1-7)
 * @param {number} weeklyRate - Weekly SSP rate (default: current rate)
 * @returns {number} Daily SSP rate
 */
export function calculateDailySSPRate(qualifyingDays, weeklyRate = SSP_WEEKLY_RATE) {
  return weeklyRate / Math.max(1, Math.min(7, qualifyingDays));
}

/**
 * Calculate SSP for an individual absence - CURRENT RULES
 * @param {object} params - Calculation parameters
 * @returns {object} SSP calculation result
 */
export function calculateSSPCurrent({
  awe,
  qualifyingDays,
  totalSickDays,
  rosteredDays, // Days actually on rota during sickness
  isLinkedPeriod = false, // If linked to previous PIW, no waiting days
  previousSSPWeeks = 0, // Weeks of SSP already paid in current/linked PIW
}) {
  const MAX_SSP_WEEKS = 28;

  // Check 28-week maximum
  if (previousSSPWeeks >= MAX_SSP_WEEKS) {
    return {
      eligible: false,
      reason: 'SSP exhausted (28-week maximum reached)',
      sspDaysPaid: 0,
      dailyRate: 0,
      totalSSP: 0,
      waitingDays: 0,
      weeksUsed: previousSSPWeeks,
      weeksRemaining: 0,
      maxReached: true,
    };
  }

  // Check eligibility
  const eligible = awe >= LOWER_EARNINGS_LIMIT;

  if (!eligible) {
    return {
      eligible: false,
      reason: 'Below Lower Earnings Limit',
      sspDaysPaid: 0,
      dailyRate: 0,
      totalSSP: 0,
      waitingDays: 0,
      weeksUsed: previousSSPWeeks,
      weeksRemaining: MAX_SSP_WEEKS - previousSSPWeeks,
    };
  }

  // Must be off for 4+ consecutive days to qualify (PIW)
  if (totalSickDays < 4) {
    return {
      eligible: true,
      reason: 'Period of Incapacity for Work not met (< 4 days)',
      sspDaysPaid: 0,
      dailyRate: 0,
      totalSSP: 0,
      waitingDays: 0,
      weeksUsed: previousSSPWeeks,
      weeksRemaining: MAX_SSP_WEEKS - previousSSPWeeks,
    };
  }

  const dailyRate = calculateDailySSPRate(qualifyingDays);
  const waitingDays = isLinkedPeriod ? 0 : Math.min(WAITING_DAYS_CURRENT, rosteredDays);
  let sspDaysPaid = Math.max(0, rosteredDays - waitingDays);

  // Apply 28-week cap
  const remainingDays = (MAX_SSP_WEEKS - previousSSPWeeks) * qualifyingDays;
  const cappedDays = Math.min(sspDaysPaid, remainingDays);
  const wasCapped = cappedDays < sspDaysPaid;
  sspDaysPaid = cappedDays;

  const totalSSP = sspDaysPaid * dailyRate;
  const weeksUsed = previousSSPWeeks + (sspDaysPaid / qualifyingDays);

  return {
    eligible: true,
    reason: null,
    dailyRate,
    waitingDays,
    rosteredDays,
    sspDaysPaid,
    totalSSP,
    weeklyRate: SSP_WEEKLY_RATE,
    weeksUsed,
    weeksRemaining: MAX_SSP_WEEKS - weeksUsed,
    wasCapped,
    maxReached: weeksUsed >= MAX_SSP_WEEKS,
  };
}

/**
 * Calculate SSP for an individual absence - APRIL 2026 RULES
 * @param {object} params - Calculation parameters
 * @returns {object} SSP calculation result
 */
export function calculateSSP2026({
  awe,
  qualifyingDays,
  totalSickDays,
  rosteredDays,
  previousSSPWeeks = 0, // Track cumulative SSP weeks used in this PIW or tax year
}) {
  const MAX_SSP_WEEKS = 28;

  // Check 28-week maximum first
  if (previousSSPWeeks >= MAX_SSP_WEEKS) {
    return {
      eligible: false,
      reason: 'SSP exhausted (28-week maximum reached)',
      sspDaysPaid: 0,
      dailyRate: 0,
      weeklyRate: 0,
      totalSSP: 0,
      waitingDays: 0,
      weeksUsed: previousSSPWeeks,
      weeksRemaining: 0,
      maxReached: true,
    };
  }

  // 2026: Everyone qualifies (no LEL)
  // But lower earners get 80% of earnings capped at standard rate

  let weeklyRate = SSP_WEEKLY_RATE_2026; // Use 2026 rate
  let rateReason = 'Standard rate (2026)';

  if (awe < EIGHTY_PERCENT_THRESHOLD_2026) {
    const eightyPercent = awe * NEW_EARNINGS_CAP_PERCENT;
    weeklyRate = Math.min(eightyPercent, SSP_WEEKLY_RATE_2026);
    rateReason = `80% of AWE (£${awe.toFixed(2)})`;
  }

  const dailyRate = calculateDailySSPRate(qualifyingDays, weeklyRate);

  // 2026: No waiting days - pay from day 1
  // No PIW threshold - any sickness absence qualifies
  let sspDaysPaid = rosteredDays;

  // Apply 28-week cap
  const remainingDays = (MAX_SSP_WEEKS - previousSSPWeeks) * qualifyingDays;
  const cappedDays = Math.min(sspDaysPaid, remainingDays);
  const wasCapped = cappedDays < sspDaysPaid;
  sspDaysPaid = cappedDays;

  const totalSSP = sspDaysPaid * dailyRate;
  const weeksUsed = previousSSPWeeks + (sspDaysPaid / qualifyingDays);

  return {
    eligible: true,
    reason: null,
    dailyRate,
    weeklyRate,
    rateReason,
    waitingDays: WAITING_DAYS_2026,
    rosteredDays,
    sspDaysPaid,
    totalSSP,
    isReducedRate: awe < LOWER_EARNINGS_LIMIT,
    weeksUsed,
    weeksRemaining: MAX_SSP_WEEKS - weeksUsed,
    wasCapped,
    maxReached: weeksUsed >= MAX_SSP_WEEKS,
  };
}

/**
 * Calculate backfill cost for an absence
 * @param {object} params - Backfill parameters
 * @returns {object} Backfill cost breakdown
 */
export function calculateBackfillCost({
  rosteredDays,
  shiftLength, // Average shift length in hours
  hourlyRate,
  backfillMethod = 'same-rate', // 'same-rate' | 'overtime' | 'agency'
  agencyMarkup = 1.3,
  overtimePremium = 1.25,
  customRate = null,
}) {
  let effectiveRate = hourlyRate;
  let method = backfillMethod;
  
  if (customRate !== null) {
    effectiveRate = customRate;
    method = 'custom';
  } else {
    switch (backfillMethod) {
      case 'overtime':
        effectiveRate = hourlyRate * overtimePremium;
        break;
      case 'agency':
        effectiveRate = hourlyRate * agencyMarkup;
        break;
      default:
        effectiveRate = hourlyRate;
    }
  }
  
  const totalHours = rosteredDays * shiftLength;
  const totalCost = totalHours * effectiveRate;
  
  return {
    method,
    effectiveRate,
    totalHours,
    totalCost,
    breakdown: {
      days: rosteredDays,
      hoursPerDay: shiftLength,
      ratePerHour: effectiveRate,
    },
  };
}

/**
 * Calculate total absence cost (SSP + Backfill)
 * @param {object} sspResult - Result from calculateSSPCurrent or calculateSSP2026
 * @param {object} backfillResult - Result from calculateBackfillCost
 * @returns {object} Total cost breakdown
 */
export function calculateTotalAbsenceCost(sspResult, backfillResult) {
  return {
    sspCost: sspResult.totalSSP,
    backfillCost: backfillResult.totalCost,
    totalCost: sspResult.totalSSP + backfillResult.totalCost,
    breakdown: {
      ssp: sspResult,
      backfill: backfillResult,
    },
  };
}

/**
 * Calculate workforce-level SSP costs for a cohort
 * @param {object} cohort - Cohort configuration
 * @param {object} sicknessPattern - Sickness pattern configuration
 * @param {object} options - Additional options
 * @returns {object} Workforce cost analysis
 */
export function calculateCohortCosts(cohort, sicknessPattern, options = {}) {
  const {
    includeBackfill = true,
    backfillMethod = 'same-rate',
  } = options;
  
  const { count, contractedHours, avgPickupHours, hourlyRate, qualifyingDays, typicalShiftLength, rotaProbability } = cohort;
  const { avgEpisodesPerEmployee, distribution, avgLengths } = sicknessPattern;
  
  // Calculate weekly earnings
  const weeklyHours = contractedHours + avgPickupHours;
  const weeklyEarnings = weeklyHours * hourlyRate;
  const awe = weeklyEarnings;
  
  // Total episodes for this cohort
  const totalEpisodes = count * avgEpisodesPerEmployee;
  
  // Episodes by duration
  const episodes = {
    oneDay: totalEpisodes * (distribution.oneDay / 100),
    twoDay: totalEpisodes * (distribution.twoDay / 100),
    threeDay: totalEpisodes * (distribution.threeDay / 100),
    fourToSeven: totalEpisodes * (distribution.fourToSeven / 100),
    eightPlus: totalEpisodes * (distribution.eightPlus / 100),
  };
  
  // Total sick days
  const totalSickDays = 
    episodes.oneDay * 1 +
    episodes.twoDay * 2 +
    episodes.threeDay * 3 +
    episodes.fourToSeven * avgLengths.fourToSeven +
    episodes.eightPlus * avgLengths.eightPlus;
  
  // Rostered days (based on probability)
  const rosteredSickDays = totalSickDays * rotaProbability;
  
  // CURRENT RULES
  const meetsLEL = awe >= LOWER_EARNINGS_LIMIT;
  const dailyRateCurrent = calculateDailySSPRate(qualifyingDays);
  
  let currentSSPDays = 0;
  let currentSSPCost = 0;
  
  if (meetsLEL) {
    // Only 4+ day absences qualify, and only days after waiting period
    // 1-3 day: 0 days paid
    // 4-7 day: (avg length - 3 waiting) * rota probability * episode count
    // 8+ day: (avg length - 3 waiting) * rota probability * episode count

    const fourToSevenRosteredPaid = episodes.fourToSeven *
      Math.max(0, (avgLengths.fourToSeven - WAITING_DAYS_CURRENT)) * rotaProbability;
    const eightPlusRosteredPaid = episodes.eightPlus *
      Math.max(0, (avgLengths.eightPlus - WAITING_DAYS_CURRENT)) * rotaProbability;

    currentSSPDays = fourToSevenRosteredPaid + eightPlusRosteredPaid;
    currentSSPCost = currentSSPDays * dailyRateCurrent;
  }
  
  // 2026 RULES
  let weeklyRate2026 = SSP_WEEKLY_RATE_2026;
  if (awe < EIGHTY_PERCENT_THRESHOLD_2026) {
    weeklyRate2026 = Math.min(awe * NEW_EARNINGS_CAP_PERCENT, SSP_WEEKLY_RATE_2026);
  }
  const dailyRate2026 = calculateDailySSPRate(qualifyingDays, weeklyRate2026);
  
  // All rostered sick days are paid from day 1
  const newSSPDays = rosteredSickDays;
  const newSSPCost = newSSPDays * dailyRate2026;
  
  // Backfill costs (same for both scenarios)
  let backfillCost = 0;
  if (includeBackfill) {
    backfillCost = rosteredSickDays * typicalShiftLength * hourlyRate;
    if (backfillMethod === 'overtime') {
      backfillCost *= 1.25;
    } else if (backfillMethod === 'agency') {
      backfillCost *= 1.3;
    }
  }
  
  return {
    cohort: cohort.name,
    employeeCount: count,
    weeklyEarnings: awe,
    meetsLEL,
    
    totalEpisodes,
    totalSickDays,
    rosteredSickDays,
    
    current: {
      eligible: meetsLEL,
      dailyRate: dailyRateCurrent,
      weeklyRate: SSP_WEEKLY_RATE,
      daysPaid: currentSSPDays,
      sspCost: currentSSPCost,
      backfillCost,
      totalCost: currentSSPCost + backfillCost,
    },
    
    new2026: {
      eligible: true,
      dailyRate: dailyRate2026,
      weeklyRate: weeklyRate2026,
      isReducedRate: awe < LOWER_EARNINGS_LIMIT,
      daysPaid: newSSPDays,
      sspCost: newSSPCost,
      backfillCost,
      totalCost: newSSPCost + backfillCost,
    },
    
    impact: {
      additionalDaysPaid: newSSPDays - currentSSPDays,
      additionalSSPCost: newSSPCost - currentSSPCost,
      percentIncrease: currentSSPCost > 0 
        ? ((newSSPCost - currentSSPCost) / currentSSPCost) * 100 
        : (newSSPCost > 0 ? Infinity : 0),
    },
  };
}

/**
 * Calculate totals across all cohorts
 * @param {Array} cohortResults - Array of cohort calculation results
 * @returns {object} Aggregated totals
 */
export function aggregateCohortResults(cohortResults) {
  const totals = cohortResults.reduce((acc, c) => {
    acc.employeeCount += c.employeeCount;
    acc.totalEpisodes += c.totalEpisodes;
    acc.totalSickDays += c.totalSickDays;
    acc.rosteredSickDays += c.rosteredSickDays;
    acc.currentSSPDays += c.current.daysPaid;
    acc.currentSSPCost += c.current.sspCost;
    acc.currentBackfillCost += c.current.backfillCost;
    acc.currentTotalCost += c.current.totalCost;
    acc.newSSPDays += c.new2026.daysPaid;
    acc.newSSPCost += c.new2026.sspCost;
    acc.newBackfillCost += c.new2026.backfillCost;
    acc.newTotalCost += c.new2026.totalCost;
    acc.employeesBelowLEL += c.meetsLEL ? 0 : c.employeeCount;
    return acc;
  }, {
    employeeCount: 0,
    totalEpisodes: 0,
    totalSickDays: 0,
    rosteredSickDays: 0,
    currentSSPDays: 0,
    currentSSPCost: 0,
    currentBackfillCost: 0,
    currentTotalCost: 0,
    newSSPDays: 0,
    newSSPCost: 0,
    newBackfillCost: 0,
    newTotalCost: 0,
    employeesBelowLEL: 0,
  });
  
  // Calculate derived metrics
  totals.sspCostIncrease = totals.newSSPCost - totals.currentSSPCost;
  totals.totalCostIncrease = totals.newTotalCost - totals.currentTotalCost;
  totals.percentSSPIncrease = totals.currentSSPCost > 0 
    ? (totals.sspCostIncrease / totals.currentSSPCost) * 100 
    : 0;
  totals.additionalDaysPaid = totals.newSSPDays - totals.currentSSPDays;
  totals.currentCostPerEmployee = totals.employeeCount > 0 
    ? totals.currentSSPCost / totals.employeeCount 
    : 0;
  totals.newCostPerEmployee = totals.employeeCount > 0 
    ? totals.newSSPCost / totals.employeeCount 
    : 0;
  
  return totals;
}

/**
 * Convert rota probability to shifts per week
 * @param {number} probability - Probability (0-1)
 * @returns {number} Approximate shifts per week
 */
export function probabilityToShifts(probability) {
  return probability * 7;
}

/**
 * Convert shifts per week to rota probability
 * @param {number} shiftsPerWeek - Shifts per week
 * @returns {number} Probability (0-1)
 */
export function shiftsToProability(shiftsPerWeek) {
  return Math.min(1, Math.max(0, shiftsPerWeek / 7));
}

/**
 * Format currency for display
 * @param {number} amount - Amount in pounds
 * @param {boolean} showPence - Whether to show pence
 * @returns {string} Formatted currency string
 */
export function formatCurrency(amount, showPence = true) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: showPence ? 2 : 0,
    maximumFractionDigits: showPence ? 2 : 0,
  }).format(amount);
}

/**
 * Format percentage for display
 * @param {number} value - Percentage value
 * @param {number} decimals - Decimal places
 * @returns {string} Formatted percentage string
 */
export function formatPercent(value, decimals = 1) {
  if (!isFinite(value)) return 'N/A';
  return `${value >= 0 ? '+' : ''}${value.toFixed(decimals)}%`;
}
